/**
 * EventSelector Component
 *
 * A dropdown selector for choosing HMC events.
 * Used in the Request Details modal for changing Primary/Backup event selections.
 *
 * Created: 2026-01-25
 */

import React, { useState, useRef, useEffect, useMemo } from 'react';
import { format, parseISO } from 'date-fns';
import {
  Calendar,
  ChevronDown,
  X,
  Search,
  MapPin,
  Check,
} from 'lucide-react';
import { type HMCEvent } from '../../hmcEventsStore';

interface EventSelectorProps {
  /** Currently selected event */
  selectedEvent: {
    id?: number;
    name: string;
    date?: string;
    venue?: string | null;
  } | null;
  /** List of available events to choose from */
  events: HMCEvent[];
  /** Callback when an event is selected */
  onSelect: (event: HMCEvent | null) => void;
  /** Placeholder text when no event selected */
  placeholder?: string;
  /** Whether the selector is disabled */
  disabled?: boolean;
  /** Whether to show a clear button */
  allowClear?: boolean;
  /** Label for the selector */
  label?: string;
  /** Compact mode for inline display */
  compact?: boolean;
}

/**
 * Format date for display
 */
function formatEventDate(dateStr: string | null | undefined): string {
  if (!dateStr) return '';
  try {
    return format(parseISO(dateStr), 'MMM d, yyyy');
  } catch {
    return dateStr;
  }
}

/**
 * Get display name for an event
 */
function getEventDisplayName(event: HMCEvent): string {
  return event.opponent || event.special_event || `Event #${event.id}`;
}

export const EventSelector: React.FC<EventSelectorProps> = ({
  selectedEvent,
  events,
  onSelect,
  placeholder = 'Select an event...',
  disabled = false,
  allowClear = false,
  label,
  compact = false,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // Filter events based on search query
  const filteredEvents = useMemo(() => {
    if (!searchQuery.trim()) return events;

    const query = searchQuery.toLowerCase();
    return events.filter((event) => {
      const name = getEventDisplayName(event).toLowerCase();
      const date = event.event_date?.toLowerCase() || '';
      const venue = event.venue?.toLowerCase() || '';
      return name.includes(query) || date.includes(query) || venue.includes(query);
    });
  }, [events, searchQuery]);

  // Sort events by date (upcoming first)
  const sortedEvents = useMemo(() => {
    return [...filteredEvents].sort((a, b) => {
      const dateA = a.event_date ? new Date(a.event_date).getTime() : 0;
      const dateB = b.event_date ? new Date(b.event_date).getTime() : 0;
      return dateA - dateB;
    });
  }, [filteredEvents]);

  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false);
        setSearchQuery('');
      }
    }

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [isOpen]);

  // Focus search input when dropdown opens
  useEffect(() => {
    if (isOpen && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isOpen]);

  const handleSelect = (event: HMCEvent) => {
    onSelect(event);
    setIsOpen(false);
    setSearchQuery('');
  };

  const handleClear = (e: React.MouseEvent) => {
    e.stopPropagation();
    onSelect(null);
  };

  const handleToggle = () => {
    if (!disabled) {
      setIsOpen(!isOpen);
      if (!isOpen) {
        setSearchQuery('');
      }
    }
  };

  // Find the matching HMCEvent for the selected event
  const matchingEvent = selectedEvent?.id
    ? events.find((e) => e.id === selectedEvent.id)
    : selectedEvent?.name
      ? events.find((e) => getEventDisplayName(e) === selectedEvent.name)
      : null;

  return (
    <div ref={containerRef} className="relative">
      {label && (
        <label className="block text-xs font-medium text-dark-muted mb-1">{label}</label>
      )}

      {/* Trigger Button */}
      <button
        type="button"
        onClick={handleToggle}
        disabled={disabled}
        className={`
          w-full flex items-center justify-between gap-2
          ${compact ? 'px-2 py-1 text-xs' : 'px-3 py-2 text-sm'}
          bg-dark-surface border border-dark-border/30 rounded-xl
          text-left transition-all duration-150
          ${disabled ? 'opacity-50 cursor-not-allowed' : 'hover:bg-dark-input hover:border-dark-border/30'}
          ${isOpen ? 'ring-2 ring-purple-500 border-purple-500' : ''}
        `}
      >
        <div className="flex items-center gap-2 min-w-0 flex-1">
          <Calendar className={`${compact ? 'h-3 w-3' : 'h-4 w-4'} text-purple-400 flex-shrink-0`} />
          {selectedEvent ? (
            <div className="truncate">
              <span className="text-white font-medium">{selectedEvent.name}</span>
              {selectedEvent.date && (
                <span className="text-dark-muted ml-2">
                  {formatEventDate(selectedEvent.date)}
                </span>
              )}
            </div>
          ) : (
            <span className="text-dark-muted">{placeholder}</span>
          )}
        </div>

        <div className="flex items-center gap-1 flex-shrink-0">
          {allowClear && selectedEvent && !disabled && (
            <button
              type="button"
              onClick={handleClear}
              className="p-1 rounded hover:bg-dark-surface/40 text-dark-muted hover:text-white transition-all duration-150"
              title="Clear selection"
            >
              <X className="h-3 w-3" />
            </button>
          )}
          <ChevronDown
            className={`${compact ? 'h-3 w-3' : 'h-4 w-4'} text-dark-muted transition-transform ${isOpen ? 'rotate-180' : ''}`}
          />
        </div>
      </button>

      {/* Dropdown Menu */}
      {isOpen && (
        <div
          className="absolute z-50 mt-1 w-full min-w-[280px] bg-dark-bg border border-dark-border/30 rounded-xl shadow-2xl shadow-black/20 overflow-hidden"
          style={{ maxHeight: '320px' }}
        >
          {/* Search Input */}
          <div className="p-2 border-b border-dark-border/30">
            <div className="relative">
              <Search className="absolute left-2.5 top-1/2 transform -translate-y-1/2 h-4 w-4 text-dark-muted" />
              <input
                ref={inputRef}
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search events..."
                className="w-full pl-8 pr-3 py-1.5 text-sm bg-dark-surface border border-dark-border/30 rounded-md text-white placeholder-dark-muted focus:outline-none focus:ring-1 focus:ring-purple-500 focus:border-purple-500"
              />
            </div>
          </div>

          {/* Events List */}
          <div className="overflow-y-auto" style={{ maxHeight: '260px' }}>
            {sortedEvents.length === 0 ? (
              <div className="px-3 py-4 text-center text-dark-muted text-sm">
                {searchQuery ? 'No events match your search' : 'No events available'}
              </div>
            ) : (
              sortedEvents.map((event) => {
                const isSelected = matchingEvent?.id === event.id;
                const displayName = getEventDisplayName(event);

                return (
                  <button
                    key={event.id}
                    type="button"
                    onClick={() => handleSelect(event)}
                    className={`
                      w-full px-3 py-2 text-left transition-all duration-150 flex items-start gap-3
                      ${isSelected ? 'bg-purple-900/30 text-white' : 'text-dark-text hover:bg-dark-surface'}
                    `}
                  >
                    {/* Selection indicator */}
                    <div className={`flex-shrink-0 mt-0.5 w-4 h-4 rounded-full border ${isSelected ? 'bg-purple-500 border-purple-500' : 'border-dark-border/30'} flex items-center justify-center`}>
                      {isSelected && <Check className="h-2.5 w-2.5 text-white" />}
                    </div>

                    {/* Event info */}
                    <div className="flex-1 min-w-0">
                      <div className="font-medium text-sm truncate">{displayName}</div>
                      <div className="flex items-center gap-2 text-xs text-dark-muted mt-0.5">
                        {event.event_date && (
                          <span className="flex items-center gap-1">
                            <Calendar className="h-3 w-3" />
                            {formatEventDate(event.event_date)}
                          </span>
                        )}
                        {event.venue && (
                          <span className="flex items-center gap-1 truncate">
                            <MapPin className="h-3 w-3" />
                            {event.venue}
                          </span>
                        )}
                      </div>
                    </div>
                  </button>
                );
              })
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default EventSelector;
